/**
 * Walk it through, steps 2 and 3: the fields of a moment and of the offer, named after what the guest's card
 * shows and in the order it shows them. They read and write the same card the room draws, so a word typed here
 * is on the card at once and a word typed on the card is here.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { BOARD_CAPS, BOARD_LIMITS } from "../../../shared/board-caps";
import type { BoardCard, BoardCards } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { REACTIONS } from "../../../shared/live-reactions";
import { mmss, secondsAt } from "../make/time";

type Moment = BoardCards["poll"] | BoardCards["question"] | BoardCards["reaction"] | BoardCards["handout"];
type Offer = BoardCards["offer"];

/** What happens when a guest presses the offer, by the plain names the picker shows. */
export const OFFER_KINDS: ReadonlyArray<{ type: Offer["offerType"]; label: string }> = [
  { type: "call", label: "Book a call" },
  { type: "buy", label: "Buy" },
  { type: "code", label: "Enter a code" },
  { type: "download", label: "Download" },
  { type: "message", label: "Just a message" },
];

/** "Only for two minutes": the offer's window, in seconds. */
const OFFER_WINDOW_SEC = 120;

/** How the fields change a card, and how that change is taken back from the bar. */
interface FieldEdits {
  /** Typing is off while Before is held. */
  off: boolean;
  edit: (id: string, patch: Record<string, unknown>) => void;
  /** A field's typing ended with a change: the bar's Undo puts it back as it was. */
  typed: (id: string, back: (card: BoardCard) => Record<string, unknown>) => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bw-fld">
      <span>{label}</span>
      {children}
    </div>
  );
}

/** One line of words: typed into, it is on the card at once; leaving it with a change makes it one Undo. */
function Words({ label, value, max, off, onType, onDone, placeholder, spellCheck = true }: { label: string; value: string; max: number; off: boolean; onType: (v: string) => void; onDone: (was: string) => void; placeholder?: string; spellCheck?: boolean }) {
  const was = useRef(value);
  return (
    <input dir="auto"
      className="inp"
      aria-label={label}
      value={value}
      maxLength={max}
      placeholder={placeholder}
      spellCheck={spellCheck}
      disabled={off}
      onFocus={() => (was.current = value)}
      onChange={(e) => onType(e.target.value)}
      onBlur={() => was.current !== value && onDone(was.current)}
    />
  );
}

/** The second a card shows at, typed as the room reads it: 50:12, or a minute in a live. */
function ShowsAt({ t, live, off, onTime }: { t: number; live: boolean; off: boolean; onTime: (sec: number) => void }) {
  const shown = live ? String(Math.round(t / 60)) : mmss(t);
  const [draft, setDraft] = useState<string | null>(null);
  const take = () => {
    const typed = (draft ?? "").trim();
    const sec = live ? (/^\d+$/.test(typed) ? Number(typed) * 60 : null) : secondsAt(typed.includes(":") ? typed : `${typed}:00`);
    setDraft(null);
    if (sec !== null && sec !== t) onTime(sec);
  };
  return (
    <span className="bw-showat">
      <span>{live ? "About minute" : "At"}</span>
      <input dir="auto"
        className="inp nbtime"
        aria-label={live ? "Minute it shows" : "Second it shows"}
        value={draft ?? shown}
        inputMode="numeric"
        spellCheck={false}
        autoComplete="off"
        disabled={off}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={take}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
    </span>
  );
}

interface FilePick {
  has: string;
  sub?: string;
  thumb?: string | null;
  onAdd: () => void;
  onOff?: () => void;
}

/** A field holding one file: what is there now, Assets to put another, and a way to take it off. */
function FileField({ label, thumb, has, sub, onAdd, onOff }: FilePick & { label: string }) {
  return (
    <Field label={label}>
      {has ? (
        <div className="bw-filerow">
          {thumb ? (
            <span className="fl-athumb">
              <img src={thumb} alt="" />
            </span>
          ) : null}
          <span className="sp">
            <b>{has}</b>
            {sub ? <em>{sub}</em> : null}
          </span>
          {onOff ? (
            <button type="button" className="ibtn sm" aria-label={`Take ${label.toLowerCase()} off`} onClick={onOff}>
              <Icon name="x" />
            </button>
          ) : null}
        </div>
      ) : null}
      <button type="button" className="btn s" onClick={onAdd}>
        <Icon name="plus" /> From Assets
      </button>
    </Field>
  );
}

interface OfferFieldsProps extends FieldEdits {
  offer: Offer;
  live: boolean;
  onTime: (sec: number) => void;
  file: FilePick;
  videoNote: FilePick;
}

/** The offer's fields, in the order its card shows them. */
export function OfferFields({ offer, live, off, edit, typed, onTime, file, videoNote }: OfferFieldsProps) {
  const patch = (p: Record<string, unknown>) => edit(offer.id, p);
  const word = (field: "text" | "btn" | "code" | "url", max: number) => ({
    value: offer[field] ?? "",
    max,
    off,
    onType: (v: string) => patch({ [field]: v }),
    onDone: (was: string) => typed(offer.id, () => ({ [field]: was })),
  });
  const kind = offer.offerType;
  return (
    <>
      <Field label="Headline">
        <Words label="Headline" {...word("text", BOARD_CAPS.prose)} />
      </Field>
      {kind === "call" || kind === "buy" || kind === "download" ? (
        <Field label="Button">
          <Words label="Button" {...word("btn", BOARD_CAPS.offerBtn)} />
        </Field>
      ) : null}
      <Field label="When they press it">
        <select
          className="inp"
          aria-label="When they press it"
          value={kind}
          disabled={off}
          onChange={(e) => {
            const was = offer.offerType;
            patch({ offerType: e.target.value });
            typed(offer.id, () => ({ offerType: was }));
          }}
        >
          {OFFER_KINDS.map((k) => (
            <option key={k.type} value={k.type}>
              {k.label}
            </option>
          ))}
        </select>
      </Field>
      {kind === "call" || kind === "buy" ? (
        <Field label="The button opens">
          <Words label="The button opens" placeholder="https://" spellCheck={false} {...word("url", BOARD_CAPS.line)} />
        </Field>
      ) : null}
      {kind === "code" ? (
        <>
          <Field label="Code">
            <Words label="Code" spellCheck={false} {...word("code", BOARD_CAPS.offerBtn)} />
          </Field>
          <Field label="Where to use it">
            <Words label="Where to use it" placeholder="yourwebsite.com" spellCheck={false} {...word("url", BOARD_CAPS.line)} />
          </Field>
        </>
      ) : null}
      {kind === "download" ? <FileField label="File" {...file} /> : null}
      <Field label="Shows">
        <span className="bw-shows">
          <ShowsAt t={offer.t ?? 0} live={live} off={off} onTime={onTime} />
          <select
            className="inp"
            aria-label="How long it stays"
            value={typeof offer.showFor === "number" ? "two" : "end"}
            disabled={off}
            onChange={(e) => {
              const was = offer.showFor;
              patch({ showFor: e.target.value === "two" ? OFFER_WINDOW_SEC : undefined });
              typed(offer.id, () => ({ showFor: was }));
            }}
          >
            <option value="end">and stays to the end</option>
            <option value="two">and stays for two minutes</option>
          </select>
        </span>
      </Field>
      <FileField label="Video note" {...videoNote} />
    </>
  );
}

/** The reactions guests can tap, picked from the set the room offers, in the order the card shows them; at least one stays. */
function ReactionPicks({ emojis, off, onChange }: { emojis: readonly string[]; off: boolean; onChange: (next: string[]) => void }) {
  const set = REACTIONS.map((r) => ({ emoji: r.emoji, label: r.label }));
  const extra = emojis.filter((e) => !set.some((r) => r.emoji === e)).map((e) => ({ emoji: e, label: e }));
  const options = [...extra, ...set];
  return (
    <span className="bw-picks-row" role="group" aria-label="Reactions">
      {options.map((o) => {
        const on = emojis.includes(o.emoji);
        return (
          <button
            type="button"
            key={o.emoji}
            className={cx("bw-rpick", on && "on")}
            aria-label={o.label}
            aria-pressed={on}
            title={o.label}
            disabled={off || (on && emojis.length === 1)}
            onClick={() => onChange(options.map((x) => x.emoji).filter((e) => (e === o.emoji ? !on : emojis.includes(e))))}
          >
            {o.emoji}
          </button>
        );
      })}
    </span>
  );
}

interface MomentFieldsProps extends FieldEdits {
  moment: Moment;
  live: boolean;
  onTime: (sec: number) => void;
  file: FilePick;
}

/** A moment's fields, named after what the guest sees. */
export function MomentFields({ moment: m, live, off, edit, typed, onTime, file }: MomentFieldsProps) {
  const patch = (p: Record<string, unknown>) => edit(m.id, p);
  const text = (field: string, label: string, value: string, max: number) => (
    <Words label={label} value={value} max={max} off={off} onType={(v) => patch({ [field]: v })} onDone={(was) => typed(m.id, () => ({ [field]: was }))} />
  );
  const shows = (
    <Field label="Shows">
      <ShowsAt t={m.t ?? 0} live={live} off={off} onTime={onTime} />
    </Field>
  );
  if (m.kind === "poll") {
    const opts = m.opts;
    return (
      <>
        <Field label="Question">{text("q", "Question", m.q, BOARD_CAPS.line)}</Field>
        <Field label="Answers">
          {opts.map((o, i) => (
            <span className="bw-ansrow" key={i}>
              <Words
                label={`Answer ${i + 1}`}
                value={o}
                max={BOARD_CAPS.option}
                off={off}
                onType={(v) => patch({ opts: opts.map((x, k) => (k === i ? v : x)) })}
                onDone={(was) => typed(m.id, (c) => ({ opts: (c as BoardCards["poll"]).opts.map((x, k) => (k === i ? was : x)) }))}
              />
              {opts.length > 2 ? (
                <button
                  type="button"
                  className="ibtn sm"
                  aria-label={`Take out answer ${i + 1}`}
                  disabled={off}
                  onClick={() => {
                    patch({ opts: opts.filter((_, k) => k !== i) });
                    typed(m.id, () => ({ opts }));
                  }}
                >
                  <Icon name="x" />
                </button>
              ) : null}
            </span>
          ))}
          {opts.length < BOARD_LIMITS.options ? (
            <button
              type="button"
              className="btn s"
              disabled={off}
              onClick={() => {
                patch({ opts: [...opts, ""] });
                typed(m.id, () => ({ opts }));
              }}
            >
              <Icon name="plus" /> Add an answer
            </button>
          ) : null}
        </Field>
        {shows}
      </>
    );
  }
  if (m.kind === "question") {
    return (
      <>
        <Field label="Question">{text("q", "Question", m.q, BOARD_CAPS.line)}</Field>
        {shows}
      </>
    );
  }
  if (m.kind === "reaction") {
    return (
      <>
        <Field label="What they react to">{text("prompt", "What they react to", m.prompt, BOARD_CAPS.line)}</Field>
        <Field label="Reactions">
          <ReactionPicks emojis={m.emojis} off={off} onChange={(emojis) => { patch({ emojis }); typed(m.id, () => ({ emojis: m.emojis })); }} />
        </Field>
        {shows}
      </>
    );
  }
  return (
    <>
      <Field label="Name">{text("name", "Name", m.name, BOARD_CAPS.handoutName)}</Field>
      <FileField label="File" {...file} />
      {shows}
    </>
  );
}

/** The card a moment's fields sit in, by the moment: its title, a way to close it, and the one thing besides typing. */
export function FieldsCard({ title, onClose, children, foot }: { title: string; onClose: () => void; children: ReactNode; foot?: ReactNode }) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // A window open over the card (Assets, a sheet) takes Esc for itself.
      if (document.querySelector(".scrim:not(.bw-cardscrim)")) return;
      onClose();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div className={"bw-card bw-chat in"} role="dialog" aria-label={title}>
      <div className="bw-ch">
        <span className="bw-cl">{title}</span>
        <button type="button" className="ibtn sm" aria-label="Close" onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <div className="bw-cb bw-fields">{children}</div>
      {foot ? <div className="bw-cfoot">{foot}</div> : null}
    </div>
  );
}
